import { lazy, Suspense } from "react"
import { Outlet } from "react-router-dom"
import { hasModuleAccess } from "@/lib/module-access"
import { useAuthStore } from "@/stores/auth"

/**
 * THE SAME TWO-LEVEL LAZY SPLIT client-layout.tsx uses, and for the same reason.
 * A dynamic import keeps the assistant out of this layout's chunk entirely, so a
 * route that renders EditorLayout without the assistant never fetches it. That
 * property is what makes the `assistant` prop below worth having rather than
 * relying on the module gate alone.
 */
const AssistantLauncher = lazy(() =>
  import("@/components/assistant/AssistantLauncher").then((m) => ({
    default: m.AssistantLauncher,
  })),
)

/* Full-screen editor chrome — NO app sidebar.
 *
 * Used for focused editing surfaces (e.g. the Assessment Builder) that
 * provide their own top bar + step navigation, à la Notion / Linear /
 * Typeform. The page itself owns scrolling, so we lock the shell to the
 * viewport and let the editor manage overflow internally. */
export function EditorLayout({ assistant = false }: { assistant?: boolean }) {
  const user = useAuthStore((s) => s.user)
  const role = useAuthStore((s) => s.role)

  /**
   * OPT-IN PER ROUTE, NOT PER LAYOUT, and the distinction is the whole point.
   *
   * Three routes render this layout: the Assessment Builder and the client's
   * Live Feed, both CLIENT_ADMIN — and /proctor/live/:assessmentId, which is
   * not. The assistant has never existed on any of them, because it is mounted
   * once in client-layout.tsx and these routes deliberately bypass that shell
   * (App.tsx: "full-screen editor, NO app sidebar"). That was a gap from the
   * assistant's first frontend commit, not a regression — `git log -S` shows
   * this file has never contained the launcher.
   *
   * WHY A PROP RATHER THAN LEANING ON hasModuleAccess ALONE. The module gate
   * below would already return false for a proctor, so an unconditional mount
   * would render nothing for them and look correct. But React.lazy resolves its
   * import when the component RENDERS, and "renders nothing" still means the
   * decision is made at runtime inside a subtree the proctor's page has to
   * reach. Gating at the route means the proctor's bundle graph never contains
   * a reason to fetch the chunk at all. Same rule as the fix that stopped the
   * assistant shipping to candidates: do not ship what a role cannot use.
   *
   * BOTH GATES ARE KEPT. The prop answers "should this ROUTE offer it"; the
   * module check answers "may this PERSON use it". A client admin without the
   * module opens the builder and sees nothing, exactly as they see nothing in
   * the main shell.
   */
  const showAssistant = assistant && hasModuleAccess(user ?? null, role, "aiAssistant")

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-white">
      <Outlet />
      {/* Fixed-position, so it sits outside the flex flow and the layout's
          overflow-hidden does not clip it — nothing here creates a containing
          block (no transform, filter or will-change), which is what would break
          that. fallback={null} and a LOCAL boundary, matching client-layout:
          suspending up to App.tsx's boundary would blank the whole editor to
          load a floating button. */}
      {showAssistant ? (
        <Suspense fallback={null}>
          <AssistantLauncher />
        </Suspense>
      ) : null}
    </div>
  )
}
